import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  Box,
  CircularProgress
} from '@mui/material';
import { format, addDays } from 'date-fns';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SaveIcon from '@mui/icons-material/Save';
import { updateScheduledSubmission } from '../api/scheduler';

const EditScheduleDialog = ({ 
  open, 
  onClose, 
  scheduledSubmission,
  onUpdate
}) => {
  const [scheduledDate, setScheduledDate] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && scheduledSubmission) {
      // Set the current scheduled date/time as default
      const currentDate = new Date(scheduledSubmission.scheduled_for);
      setScheduledDate(format(currentDate, 'yyyy-MM-dd\'T\'HH:mm'));
      setError(null);
    }
  }, [open, scheduledSubmission]);

  const handleSubmit = async () => {
    if (!scheduledDate) {
      setError('Please select a date and time');
      return;
    }

    const newDate = new Date(scheduledDate);
    const now = new Date();

    // Validate the date is in the future
    if (newDate <= now) {
      setError('Scheduled date must be in the future');
      return;
    }

    setIsUpdating(true);
    setError(null);

    try {
      await updateScheduledSubmission(scheduledSubmission.id, {
        scheduledFor: newDate
      });

      // Success - notify parent and close
      if (onUpdate) {
        onUpdate();
      }
      onClose();
    } catch (err) {
      console.error('Failed to update scheduled submission:', err);
      setError(err.response?.data?.detail || 'Failed to update schedule');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSetQuickDate = (days) => {
    const futureDate = addDays(new Date(), days);
    // Set time to 9 AM
    futureDate.setHours(9, 0, 0, 0);
    setScheduledDate(format(futureDate, 'yyyy-MM-dd\'T\'HH:mm'));
  };

  if (!scheduledSubmission) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ScheduleIcon />
        Edit Schedule
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Image: GTIN {scheduledSubmission.image_gtin || 'Unknown'}
          </Typography>
          
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Currently scheduled for: {new Date(scheduledSubmission.scheduled_for).toLocaleString()}
          </Typography>

          <TextField
            type="datetime-local"
            value={scheduledDate}
            onChange={(e) => {
              setScheduledDate(e.target.value);
              setError(null);
            }}
            sx={{ mt: 3, mb: 2 }}
            fullWidth
            label="New Schedule Date & Time"
            InputLabelProps={{ shrink: true }}
            error={!!error}
            helperText={error}
          />

          <Typography variant="body2" color="textSecondary" gutterBottom>
            Quick select:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button 
              size="small" 
              variant="outlined" 
              onClick={() => handleSetQuickDate(1)}
            >
              Tomorrow 9 AM
            </Button>
            <Button 
              size="small" 
              variant="outlined" 
              onClick={() => handleSetQuickDate(7)}
            >
              Next Week
            </Button>
            <Button 
              size="small" 
              variant="outlined" 
              onClick={() => handleSetQuickDate(30)}
            >
              Next Month
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={onClose} 
          disabled={isUpdating}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={isUpdating || !scheduledDate}
          startIcon={isUpdating ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
        >
          {isUpdating ? 'Updating...' : 'Update Schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditScheduleDialog;